import { useEffect } from 'react';

import { useIntl } from 'react-intl';

import { Col, Form, Radio, Row, Select, Typography } from 'antd';
import type { FormInstance } from 'antd/es/form';

import { WORK_JOURNEY_PLACE_TYPE } from '@src/packages/workJourney/constants';
import { useWorkJourneyContext } from '@src/packages/workJourney/contexts';
import type { WorkJourneyAddress, WorkJourneyForm } from '@src/packages/workJourney/types';
import IntlMessages from '@src/util/IntlMessages';

import styles from '../../../styles.module.less';

const { Paragraph } = Typography;

const LOCATION_TYPES_WITHOUT_ADDRESS = [
  WORK_JOURNEY_PLACE_TYPE.WITHOUT_LOCATION,
  WORK_JOURNEY_PLACE_TYPE.REMOTE,
];

interface PlaceSelectorProps {
  form: FormInstance;
}

const formatAddress = (address: WorkJourneyAddress & { region?: string; city?: string }) => {
  if (!address.region) {
    return `${address.address}, ${address.city}`;
  }
  return `${address.address}, ${address.city}, ${address.region}`;
};

/**
 * @description Component to select the location type
 */
const PlaceSelector = ({ form }: PlaceSelectorProps) => {
  const { workJourneyForm, setWorkJourneyForm, address } = useWorkJourneyContext();
  const { locationType } = workJourneyForm;
  const intl = useIntl();

  const initialFormData = (data: WorkJourneyForm) => {
    if (!form) return;
    const addressId = data.address?.id || null;
    const resolvedLocationType = data?.locationType || null;

    if (addressId) form.setFieldsValue({ address: addressId });
    if (resolvedLocationType) form.setFieldsValue({ locationType: resolvedLocationType });
  };

  useEffect(() => {
    initialFormData(workJourneyForm);
  }, [workJourneyForm.address, form]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <Row className={styles.formSectionRow}>
        <Col xs={24} sm={24} md={24} lg={24} xl={24}>
          <Paragraph className={styles.descriptionText}>
            <IntlMessages id="workJourney.locationDescription" />
          </Paragraph>

          <Form.Item name="locationType" label="">
            <Radio.Group
              value={locationType}
              onChange={e => {
                if (LOCATION_TYPES_WITHOUT_ADDRESS.includes(e.target.value)) {
                  setWorkJourneyForm({
                    ...workJourneyForm,
                    locationType: e.target.value,
                    address: null,
                  });
                } else {
                  setWorkJourneyForm({ ...workJourneyForm, locationType: e.target.value });
                }
              }}
            >
              <Radio value="presential">
                <IntlMessages id="workJourney.presential" />
              </Radio>
              <Radio value="remote">
                <IntlMessages id="workJourney.remote" />
              </Radio>
              <Radio value="without_location">
                <IntlMessages id="workJourney.withoutLocation" />
              </Radio>
            </Radio.Group>
          </Form.Item>
        </Col>
      </Row>

      {!LOCATION_TYPES_WITHOUT_ADDRESS.includes(form.getFieldValue('locationType')) && (
        <Row className={styles.formSectionRow}>
          <Col xs={24} sm={12} md={12} lg={12} xl={12}>
            <Form.Item
              name="address"
              rules={[
                {
                  required: locationType === WORK_JOURNEY_PLACE_TYPE.PRESENTIAL,
                  message: intl.formatMessage({ id: 'workJourney.validation.locationRequired' }),
                },
              ]}
              label={intl.formatMessage({ id: 'workJourney.selectLocation' })}
            >
              <Select
                onChange={(value: string) => {
                  const selectedAddress = address.find(item => item.id === value);
                  setWorkJourneyForm({ ...workJourneyForm, address: selectedAddress ?? null });
                }}
              >
                {address.length > 0 &&
                  address.map(item => (
                    <Select.Option key={item.id} value={item.id}>
                      {formatAddress(item as WorkJourneyAddress & { region?: string; city?: string })}
                    </Select.Option>
                  ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>
      )}
    </>
  );
};

export default PlaceSelector;
